import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, LogOut } from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { QuoteCard } from './QuoteCard';
import { BookingCard } from './BookingCard';
import { isMissed } from './Shared';

const TABS = [{ id: 'missed', label: 'Missed' }, { id: 'quotes', label: 'Quotes' }, { id: 'bookings', label: 'Bookings' }];

export const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState(null);
  const [data, setData] = useState({ quotes: [], bookings: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [quotes, bookings] = await Promise.all([adminApi.quotes(), adminApi.bookings()]);
    setData({ quotes, bookings });
    setLoading(false);
  }, []);

  useEffect(() => { load().catch(() => { setLoading(false); toast.error('Could not load data'); }); }, [load]);

  const retry = async (kind, id) => {
    setBusyId(id);
    try {
      const r = await adminApi.retry(kind, id);
      if (r.email_status === 'sent') toast.success('Email sent'); else toast.error('Email failed again. Check MailerSend settings.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not retry right now. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const missed = { quotes: data.quotes.filter(isMissed), bookings: data.bookings.filter(isMissed) };
  const lists = { ...data, missed: [...missed.quotes, ...missed.bookings] };
  const failed = lists.missed.filter((x) => x.email_status === 'failed').length;
  const untracked = lists.missed.length - failed;
  const activeTab = tab || (lists.missed.length ? 'missed' : 'quotes');
  const shownQuotes = activeTab === 'missed' ? missed.quotes : activeTab === 'quotes' ? data.quotes : [];
  const shownBookings = activeTab === 'missed' ? missed.bookings : activeTab === 'bookings' ? data.bookings : [];

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-dashboard">
      <header className="bg-white border-b-4 border-red-600">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="" className="h-10 w-auto" />
            <span className="font-display italic uppercase text-xl font-extrabold">Owner <span className="text-red-600">Dashboard</span></span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-neutral-600" data-testid="admin-user-email">{user.email}</span>
            <button type="button" onClick={onLogout} className="inline-flex items-center gap-1 text-neutral-800 hover:text-red-600" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" />Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {failed > 0 && (
          <div className="flex items-center gap-3 bg-red-600 text-white rounded-sm px-4 py-3 mb-3" data-testid="failed-email-banner">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span className="text-sm font-semibold">{failed} lead{failed > 1 ? 's' : ''} never reached your inbox. Call them back or tap Retry email.</span>
          </div>
        )}
        {untracked > 0 && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-sm px-4 py-3 mb-3" data-testid="untracked-email-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
            <span className="text-sm font-semibold">{untracked} older lead{untracked > 1 ? 's' : ''} came in before email tracking and may have been missed. They&apos;re in the Missed tab.</span>
          </div>
        )}
        <div className="mb-3" />
        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-sm font-display uppercase tracking-wider font-bold text-sm ${activeTab === t.id ? 'bg-neutral-950 text-white' : 'bg-white border border-neutral-200 text-neutral-700 hover:border-red-600'}`}>
              {t.label} ({lists[t.id].length})
            </button>
          ))}
        </div>
        {loading && <p className="text-neutral-500">Loading…</p>}
        {!loading && shownQuotes.length + shownBookings.length === 0 && <p className="text-neutral-500" data-testid="admin-empty">Nothing here yet.</p>}
        <div className="space-y-4">
          {shownQuotes.map((q) => <QuoteCard key={q.id} q={q} busy={busyId === q.id} onRetry={(id) => retry('quotes', id)} />)}
          {shownBookings.map((b) => <BookingCard key={b.id} b={b} busy={busyId === b.id} onRetry={(id) => retry('bookings', id)} />)}
        </div>
      </main>
    </div>
  );
};
